import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Menu from '../menu/Menu';
import RTButton from '../common/RTButton';
import RTSpinner from '../common/RTSpinner';
import { apiGet } from '../../api/apiClient';
import { toastError } from '../common/toast';
import './JobReportList.css';
import tenantStorage from '../../utils/tenantStorage';

const ViewJobDetails = ({ jobIdProp, showMenu = true, onBack }) => {
  const navigate = useNavigate();
  const { jobId: jobIdParam } = useParams();
  const jobId = jobIdProp || jobIdParam;
  
  const [loading, setLoading] = useState(false);
  const [job, setJob] = useState(null);

  useEffect(() => {
    if (jobId) {
      fetchJobDetails(jobId);
    }
  }, [jobId]);

  const fetchJobDetails = async (id) => {
    setLoading(true);
    try {
      const token = tenantStorage.getItem('token');
      const response = await apiGet(`/jobs/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const apiResponse = response.data;
      if (apiResponse) {
        setJob(apiResponse);
      } else {
        toastError('Job not found');
        handleBack();
      }
    } catch (err) {
      console.error('Error loading job details:', err);
      toastError(err.response?.data?.detail || 'Failed to fetch job details');
      handleBack();
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate('/reports/jobs');
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    return date.toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatDuration = (start, end) => {
    if (!start || !end) return 'N/A';
    const startDate = new Date(start);
    const endDate = new Date(end);
    const durationMs = endDate - startDate;
    const durationMinutes = Math.floor(durationMs / 60000);
    const durationSeconds = Math.floor((durationMs % 60000) / 1000);
    return `${durationMinutes}m ${durationSeconds}s`;
  };

  const formatJobType = (jobType) => {
    if (!jobType) return '';
    // Convert snake_case to Title Case
    return jobType
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const getStatusBadgeClass = (status) => {
    const baseClass = 'job-status-badge';
    
    if (status === 'completed') {
      return `${baseClass} job-status-completed`;
    } else if (status === 'failed') {
      return `${baseClass} job-status-failed`;
    } else if (status === 'processing') {
      return `${baseClass} job-status-processing`;
    } else {
      return `${baseClass} job-status-pending`;
    }
  };

  if (loading) {
    const content = (
      <div className="job-spinner-container">
        <RTSpinner message='Loading job details...' />
      </div>
    );

    if (!showMenu) {
      return content;
    }

    return (
      <div className="rt-layout">
        <Menu className="rt-menu" activePath="/reports/jobs" />
        <div className="rt-main-content">
          {content}
        </div>
      </div>
    );
  }

  if (!job) {
    return null;
  }

  let parsedDetails = {};
  try {
    parsedDetails = typeof job.details === 'string' 
      ? JSON.parse(job.details) 
      : job.details;
  } catch (e) {
    console.error('Failed to parse job details:', e);
  }

  const content = (
    <>
      <div className="rt-title-row">
        <h2 className="rt-title">Job Details: {job.job_name}</h2>
        <div className='d-flex gap-2'>
          <RTButton
            variant="outline-primary"
            size="sm"
            onClick={handleBack}
          >
            &#8592; Back
          </RTButton>
          <RTButton
            variant="primary"
            size="sm"
            onClick={() => fetchJobDetails(jobId)}
            disabled={loading}
          >
            Refresh
          </RTButton>
        </div>
      </div>

      {/* Job Summary */}
      <div className="rt-section">
        <h5 className="mb-3" style={{ color: '#153C7F' }}>Job Summary</h5>
        <div className="row">
          <div className="col-md-6">
            <div className="job-detail-item">
              <strong>Job ID:</strong> {job.job_id}
            </div>
            <div className="job-detail-item">
              <strong>Job Type:</strong> {formatJobType(job.job_type)}
            </div>
            <div className="job-detail-item">
              <strong>Status:</strong> <span className={getStatusBadgeClass(job.status)}>{job.status}</span>
            </div>
            <div className="job-detail-item">
              <strong>Started At:</strong> {formatTimestamp(job.job_start_time)}
            </div>
            <div className="job-detail-item">
              <strong>Ended At:</strong> {formatTimestamp(job.job_end_time)}
            </div>
          </div>
          <div className="col-md-6">
            <div className="job-detail-item">
              <strong>Total Records:</strong> {job.total_records}
            </div>
            <div className="job-detail-item">
              <strong>Processed Records:</strong> {job.processed_records} {
                job.status === 'processing' && job.total_records > 0
                  ? `(${Math.round((job.processed_records / job.total_records) * 100)}%)`
                  : ''
              }
            </div>
            <div className="job-detail-item">
              <strong>Success Records:</strong> {job.success_records}
            </div>
            <div className="job-detail-item">
              <strong>Failed Records:</strong> {job.failed_records}
            </div>
            <div className="job-detail-item">
              <strong>Success Rate:</strong> {
                job.total_records > 0 
                  ? Math.round((job.success_records / job.total_records) * 100) 
                  : 0
              }%
            </div>
            <div className="job-detail-item">
              <strong>Duration:</strong> {formatDuration(job.job_start_time, job.job_end_time)}
            </div>
          </div>
        </div>
      </div>

      {/* Job Details JSON */}
      {parsedDetails && Object.keys(parsedDetails).length > 0 && (
        <div className="rt-section">
          <h5 className="mb-3" style={{ color: '#153C7F' }}>Detailed Results</h5>
          
          {parsedDetails.summary && (
            <div className="job-summary-box mb-3">
              <h6>Summary</h6>
              <div className="row">
                <div className="col-md-4">
                  <strong>Total Invoices:</strong> {parsedDetails.summary.total_invoices}
                </div>
                <div className="col-md-4">
                  <strong>Sent:</strong> {parsedDetails.summary.sent_count}
                </div>
                <div className="col-md-4">
                  <strong>Failed:</strong> {parsedDetails.summary.failed_count}
                </div>
              </div>
              <div className="row mt-2">
                <div className="col-md-4">
                  <strong>Payment Links Success:</strong> {parsedDetails.summary.payment_link_success_count}
                </div>
                <div className="col-md-4">
                  <strong>Payment Links Failed:</strong> {parsedDetails.summary.payment_link_failed_count}
                </div>
                <div className="col-md-4">
                  <strong>WhatsApp Sent:</strong> {parsedDetails.summary.whatsapp_sent_count}
                </div>
              </div>
            </div>
          )}

          {parsedDetails.results && (
            <>
              {parsedDetails.results.success && parsedDetails.results.success.length > 0 && (
                <div className="job-result-section mb-3">
                  <h6 style={{ color: '#153C7F' }}>Successful Invoices ({parsedDetails.results.success.length})</h6>
                  <div className="job-invoice-ids">
                    {parsedDetails.results.success.join(', ')}
                  </div>
                </div>
              )}

              {parsedDetails.results.payment_link_failed && parsedDetails.results.payment_link_failed.length > 0 && (
                <div className="job-result-section mb-3">
                  <h6 className="text-warning">Payment Link Failures ({parsedDetails.results.payment_link_failed.length})</h6>
                  <div className="job-invoice-ids">
                    {parsedDetails.results.payment_link_failed.join(', ')}
                  </div>
                </div>
              )}

              {parsedDetails.results.whatsapp_failed && parsedDetails.results.whatsapp_failed.length > 0 && (
                <div className="job-result-section">
                  <h6 className="text-warning">WhatsApp Failures ({parsedDetails.results.whatsapp_failed.length})</h6>
                  <div className="job-invoice-ids">
                    {parsedDetails.results.whatsapp_failed.join(', ')}
                  </div>
                </div>
              )}
            </>
          )}

          {parsedDetails.error && (
            <div className="alert alert-danger">
              <strong>Error:</strong> {parsedDetails.error}
            </div>
          )}
        </div>
      )}
    </>
  );

  if (!showMenu) {
    return content;
  }

  return (
    <div className="rt-layout">
      <Menu className="rt-menu" activePath="/reports/jobs" />
      <div className="rt-main-content">
        {content}
      </div>
    </div>
  );
};

export default ViewJobDetails;
